import React, { useCallback, useEffect, useMemo, useState } from 'react';
import Alert from '@mui/material/Alert';
import Autocomplete from '@mui/material/Autocomplete';
import Box from '@mui/material/Box';
import Button from '@mui/material/Button';
import CircularProgress from '@mui/material/CircularProgress';
import IconButton from '@mui/material/IconButton';
import Paper from '@mui/material/Paper';
import Stack from '@mui/material/Stack';
import Table from '@mui/material/Table';
import TableBody from '@mui/material/TableBody';
import TableCell from '@mui/material/TableCell';
import TableHead from '@mui/material/TableHead';
import TableRow from '@mui/material/TableRow';
import TextField from '@mui/material/TextField';
import Typography from '@mui/material/Typography';

import SettingsService from 'services/SettingsService';
import StaticAdsService from 'services/StaticAdsService';

import { ProductSummary } from './types';

// Where finalized creatives go: the Notion page holding the creatives table,
// and one Dropbox folder per product. Stored in the generic dashboard settings
// store under `static-publish` — the folder map as ONE JSON blob because store
// keys can't contain slashes or dots (they become Firebase paths).

export const PUBLISH_SETTINGS_NAMESPACE = 'static-publish';

/** Accepts a plain path OR a pasted dropbox.com/home link (the browser URL
 * when viewing the folder) and returns the folder path. Share links
 * (dropbox.com/scl/…) don't carry the path, so they're rejected with advice. */
export function folderPathFromInput(value: string): { path?: string; error?: string } {
  const raw = (value || '').trim();
  if (!raw) return { path: '' };
  const home = raw.match(/dropbox\.com\/home\/([^?#]+)/i);
  if (home) {
    const decoded = decodeURIComponent(home[1]).replace(/\/+$/, '');
    return { path: `/${decoded}` };
  }
  if (/^https?:\/\//i.test(raw)) {
    return { error: 'That looks like a share link — paste the folder path or the dropbox.com/home/… URL from your browser address bar instead.' };
  }
  return { path: raw.startsWith('/') ? raw : `/${raw}` };
}

const DEFAULT_NOTION_URL = 'https://app.notion.com/p/sbly/Marsha-s-Creatives-2eb65c2533ae809da524d2b15a67ee91';

export default function PublishingTab() {
  const service = useMemo(() => new StaticAdsService(), []);
  const settingsService = useMemo(() => new SettingsService(), []);

  const [loading, setLoading] = useState(true);
  const [products, setProducts] = useState<ProductSummary[]>([]);
  const [notionUrl, setNotionUrl] = useState('');
  const [folders, setFolders] = useState<Record<string, string>>({});
  const [addProduct, setAddProduct] = useState<ProductSummary | null>(null);
  const [saving, setSaving] = useState(false);
  const [savedFlash, setSavedFlash] = useState(false);
  const [error, setError] = useState<string | null>(null);
  // A failed settings READ replaces the form entirely: rendering the seeded
  // defaults over unloaded real settings would make them savable, silently
  // overwriting the team's actual configuration.
  const [loadError, setLoadError] = useState<string | null>(null);

  const load = useCallback(async () => {
    setLoading(true);
    setLoadError(null);
    try {
      const [prodResp, settings] = await Promise.all([
        service.listProducts(),
        settingsService.getOrThrow(PUBLISH_SETTINGS_NAMESPACE),
      ]);
      const prods = (prodResp?.products || []) as ProductSummary[];
      setProducts(prods);
      let map: Record<string, string> = {};
      if (typeof settings?.productFoldersJson === 'string' && settings.productFoldersJson.trim()) {
        try { map = JSON.parse(settings.productFoldersJson) || {}; } catch { map = {}; }
      }
      // First run: suggest the known comforter mapping and the Notion page
      // as UNSAVED drafts — visible, editable, nothing written until Save.
      if (!Object.keys(map).length) {
        const comforter = prods.find(p => /thermacool comforter/i.test(p.title));
        if (comforter) map = { [String(comforter.id)]: '/ThermaCool Comforter Creatives' };
      }
      setFolders(map);
      setNotionUrl(typeof settings?.notionPageUrl === 'string' && settings.notionPageUrl.trim()
        ? settings.notionPageUrl
        : DEFAULT_NOTION_URL);
    } catch (err: any) {
      setLoadError(err?.response?.data?.error || err?.message || 'Failed to load publishing settings');
    } finally {
      setLoading(false);
    }
  }, [service, settingsService]);

  useEffect(() => { void load(); }, [load]);

  const productById = (id: string) => products.find(p => String(p.id) === id);

  const save = async () => {
    setSaving(true);
    setError(null);
    try {
      const clean: Record<string, string> = {};
      for (const [id, value] of Object.entries(folders)) {
        const { path, error: parseError } = folderPathFromInput(value);
        if (parseError) throw new Error(`${productById(id)?.title || id}: ${parseError}`);
        if (path) clean[id] = path;
      }
      await settingsService.updateOrThrow(PUBLISH_SETTINGS_NAMESPACE, {
        notionPageUrl: notionUrl.trim(),
        productFoldersJson: JSON.stringify(clean),
      });
      setFolders(clean);
      setSavedFlash(true);
      setTimeout(() => setSavedFlash(false), 2500);
    } catch (err: any) {
      setError(err?.response?.data?.error || err?.message || 'Save failed');
    } finally {
      setSaving(false);
    }
  };

  if (loading) return <Box sx={{ display: 'flex', justifyContent: 'center', py: 6 }}><CircularProgress /></Box>;

  if (loadError) {
    return (
      <Box sx={{ maxWidth: 860 }}>
        <Typography variant="h6">Publishing</Typography>
        <Alert
          severity="error" sx={{ mt: 2 }}
          action={<Button color="inherit" size="small" onClick={() => { void load(); }}>Retry</Button>}
        >
          Couldn&apos;t load the current publishing settings — editing is disabled so defaults
          can&apos;t overwrite them. {loadError}
        </Alert>
      </Box>
    );
  }

  return (
    <Box sx={{ maxWidth: 860 }}>
      <Typography variant="h6">Publishing</Typography>
      <Typography variant="body2" color="text.secondary" sx={{ mb: 2 }}>
        Where finalized creatives go. These are team-wide settings.
      </Typography>

      {error && <Alert severity="error" sx={{ mb: 2 }}>{error}</Alert>}

      <Stack spacing={3}>
        <Box>
          <Typography variant="subtitle2" sx={{ mb: 0.5 }}>Notion creatives table</Typography>
          <TextField
            fullWidth size="small" label="Notion page URL" value={notionUrl}
            onChange={e => setNotionUrl(e.target.value)}
            helperText='The page holding the creatives table. It must be shared with the "Sbly Dash Collector" integration (page ⋯ menu → Connections).'
          />
        </Box>

        <Box>
          <Typography variant="subtitle2" sx={{ mb: 0.5 }}>Dropbox folders — one per product</Typography>
          <Typography variant="caption" color="text.secondary" sx={{ display: 'block', mb: 1 }}>
            A product with no folder here defaults to /&lt;Product name&gt; Creatives.
          </Typography>
          <Paper variant="outlined">
            <Table size="small">
              <TableHead>
                <TableRow>
                  <TableCell>Product</TableCell>
                  <TableCell>Dropbox folder path</TableCell>
                  <TableCell padding="checkbox" />
                </TableRow>
              </TableHead>
              <TableBody>
                {Object.entries(folders).map(([id, path]) => (
                  <TableRow key={id}>
                    <TableCell sx={{ width: 280 }}>
                      <Typography variant="body2">{productById(id)?.title || `Product ${id}`}</Typography>
                    </TableCell>
                    <TableCell>
                      <TextField
                        fullWidth size="small" value={path} placeholder="/Folder Name — or paste the folder's dropbox.com link"
                        error={!!folderPathFromInput(path).error}
                        helperText={folderPathFromInput(path).error
                          || (folderPathFromInput(path).path !== path && folderPathFromInput(path).path) || undefined}
                        onChange={e => setFolders(prev => ({ ...prev, [id]: e.target.value }))}
                      />
                    </TableCell>
                    <TableCell padding="checkbox">
                      <IconButton size="small" onClick={() => setFolders(prev => {
                        const next = { ...prev };
                        delete next[id];
                        return next;
                      })}>
                        ×
                      </IconButton>
                    </TableCell>
                  </TableRow>
                ))}
                <TableRow>
                  <TableCell colSpan={3}>
                    <Autocomplete
                      size="small"
                      options={products.filter(p => !folders[String(p.id)])}
                      getOptionLabel={p => p.title}
                      value={addProduct}
                      onChange={(_, value) => {
                        if (!value) { setAddProduct(null); return; }
                        setFolders(prev => ({ ...prev, [String(value.id)]: `/${value.title} Creatives` }));
                        setAddProduct(null);
                      }}
                      renderInput={params => <TextField {...params} label="Add a product mapping" />}
                      sx={{ maxWidth: 360 }}
                    />
                  </TableCell>
                </TableRow>
              </TableBody>
            </Table>
          </Paper>
        </Box>

        <Box>
          <Button variant="contained" onClick={save} disabled={saving}>
            {saving ? 'Saving…' : savedFlash ? 'Saved ✓' : 'Save publishing settings'}
          </Button>
        </Box>
      </Stack>
    </Box>
  );
}
